import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Space } from 'antd';
import { Button } from 'pink-lava-ui';
import { User, LockKeyhole } from 'lucide-react';
import nabatiLogo from '@/assets/nabati_red_icon.svg';
import loginBg from '@/assets/login_bg.jpg';
import { FormBuilder } from '@/components/ui/form/FormBuilder';
import type { FormField } from '@/types/FieldSchema.type';
import { useAuth } from '@/context/authContext';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loginSchema, type LoginRequest } from '@dc-trans/data-layer';
import { useLoginMutation } from '@/features/auth/api/mutations';

const AuthLogin = () => {
  const loginMutation = useLoginMutation({
    disableErrorNotification: true,
  });
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get the redirect path from URL params, default to home
  const redirectTo = searchParams.get('redirect_to') || '/';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const fields: FormField<LoginRequest>[] = [
    {
      prefix: <User size={20} className="mr-1" />,
      name: 'username',
      type: 'text',
      placeholder: 'Enter username',
      required: true,
    },
    {
      prefix: <LockKeyhole size={20} className="mr-1" />,
      name: 'password',
      type: 'password',
      placeholder: 'Enter password',
      required: true,
    },
  ];

  const methods = useForm<LoginRequest>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginRequest) => {
    loginMutation.mutate(
      {
        username: data.username,
        password: data.password,
      },
      {
        onSuccess: response => {
          methods.reset();
          login(response.data);
        },
        onError: error => {
          console.log(error);
          // The error response might have a different structure than the success response
          const errorMessage =
            (error as any)?.response?.data?.message ||
            (error as any)?.response?.data?.error ||
            'Login failed. Please try again.';
          methods.setError('root', {
            message: errorMessage,
          });
        },
      }
    );
  };

  return (
    <div
      className="min-h-screen w-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: `url(${loginBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Login Card */}
      <div className="relative z-10 w-full max-w-sm">
        <div className="bg-white rounded-4xl shadow-2xl p-8 mx-auto">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <img src={nabatiLogo} alt="Nabati Logo" className="h-14 w-auto" />
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-lg font-semibold text-gray-800">DC Trans Vendor</h1>
            <p className="text-gray-500 text-sm">Please enter username & password to login</p>
          </div>

          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Space direction="vertical" size="middle" className="w-full">
              {/* Login Form */}
              <FormProvider {...methods}>
                <FormBuilder<LoginRequest> fields={fields} methods={methods} />
              </FormProvider>
              {methods.formState.errors.root?.message && (
                <Alert message={methods.formState.errors.root?.message} type="error" showIcon />
              )}
              {/* Login Button */}
              <Button
                variant="solid"
                shape="round"
                size="large"
                htmlType="submit"
                block
                loading={loginMutation.isPending}
                disabled={!methods.formState.isDirty}
              >
                Login
              </Button>
            </Space>
          </form>
        </div>
        {/* FAQ Link */}
        <div className="mt-6 text-center text-sm font-bold">
          Untuk instruksi penggunaan dan bantuan teknis, kunjungi{' '}
          <a
            href={import.meta.env.VITE_FAQ_SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Panduan Pengguna & FAQ
          </a>
        </div>
      </div>
    </div>
  );
};

export default AuthLogin;
